import React, { useState, useMemo, useEffect } from "react";
import { Dialog } from "@/components/ui";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Fan, FanActivity, FanServices } from "@/services/bedrock";
import { useQuery } from "@tanstack/react-query";

interface FanActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fan: Fan | null;
}

const ACTIVITY_TYPE_MAP: Record<string, string> = {
  review_received: "Review Received",
  form_request_sent: "Form Requested",
  form_response_submitted: "Response Submitted",
  fan_merged_from: "Merged From",
  fan_merged_into: "Merged Into",
  player_changed: "Player Changed",
};

export function FanActivityDialog({ open, onOpenChange, fan }: FanActivityDialogProps) {
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

  // Reset filter/pagination selections when opening dialog
  useEffect(() => {
    if (open) {
      setPagination(prev => {
        if (prev.page === 1 && prev.perPage === 10) return prev;
        return { page: 1, perPage: 10 };
      });
    }
  }, [open]);

  // Fetch paginated activities list
  const { data, isLoading } = useQuery({
    queryKey: ["fanActivities", fan?.id, pagination],
    queryFn: () =>
      FanServices.getFanActivities(fan!.id, {
        page: pagination.page,
        perPage: pagination.perPage,
      }),
    enabled: !!fan?.id && open,
  });

  // Table Columns Definition
  const columns = useMemo<ColumnDef<FanActivity>[]>(
    () => [
      {
        accessorKey: "activityType",
        header: "Activity",
        cell: ({ row }) => {
          const type = row.original.activityType;
          const readableName = ACTIVITY_TYPE_MAP[type] || type;
          return <span className="font-semibold text-slate-800">{readableName}</span>;
        },
      },
      {
        id: "details",
        header: "Details",
        cell: ({ row }) => {
          const act = row.original;
          const metadata = act.metadata;
          if (!metadata || Object.keys(metadata).length === 0) {
            return <span className="text-slate-400 italic text-xs font-sans">-</span>;
          }
          return (
            <div className="flex flex-wrap gap-1 text-[11px] font-sans max-w-[400px]">
              {Object.keys(metadata).map(key => {
                const val = metadata[key];
                if (typeof val === "object" || val === null || val === undefined) return null;
                return (
                  <span
                    key={key}
                    className="bg-slate-50 text-slate-600 px-2 py-0.5 rounded-md border border-slate-200/30"
                  >
                    <strong className="text-slate-400 font-mono text-[9px] uppercase mr-1">{key}:</strong>
                    {String(val)}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        id: "occurredAt",
        header: "Date & Time",
        cell: ({ row }) => {
          const act = row.original;
          const dateStr = act.occurredAt || act.createdAt;
          if (!dateStr) return <span className="text-slate-400">-</span>;
          try {
            const date = new Date(dateStr);
            return (
              <span className="text-slate-600 font-sans text-xs font-mono">
                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            );
          } catch {
            return <span className="text-slate-600 font-sans text-xs">{dateStr}</span>;
          }
        },
      },
    ],
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="3xl">
      <Dialog.Header>
        <Dialog.Title>Activity History</Dialog.Title>
        <Dialog.Description>
          Showing complete history of activities and events recorded for{" "}
          <strong>{fan?.displayName || "this contact"}</strong>.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col gap-4 py-4 max-h-[70vh]">
        {/* Activities Data Table */}
        <div className="flex-1 overflow-hidden min-h-[300px]">
          <DataTable
            columns={columns}
            data={data?.content ?? []}
            loading={isLoading}
            pagination={pagination}
            onPaginationChange={setPagination}
            pageCount={data?.pagination?.totalPages}
            rowCount={data?.pagination?.totalCount}
          />
        </div>
      </Dialog.Content>

      <Dialog.Footer>
        <button
          onClick={() => onOpenChange(false)}
          className="bg-primary hover:bg-primary/95 text-white inline-flex items-center justify-center rounded-lg text-sm font-semibold px-4 h-10 select-none transition-colors"
          type="button"
        >
          Close
        </button>
      </Dialog.Footer>
    </Dialog>
  );
}
